import { Application } from "pixi.js";
import { ConfigParams } from "../GameConfig";
import { SceneManager } from "../scenes/SceneManager";
import GlobalStore from "./GlobalStore";
import { WebSocketClient } from "./WebSocketClient";
import { WebSocketMessageHandler } from "./WebSocketMessageHandler";
import Scene from "../scenes/Scene";
import SceneHistory from "../scenes/SceneHistory";
import SceneCurrentJackpot from "../scenes/SceneCurrentJackpot";
import SceneJackpotHit from "../scenes/SceneJackpotHit";
import { JackpotHitData } from "../models/JackpotHitData";
import { GameEvents, MessageEvents } from "../events/EventEnums";
import {
  JackpotHistoryMessage,
  JackpotHitMessage,
  MessageType,
  ParametersMessage,
  ProgressiveCurValuesMessage,
} from "../models/Messages";
import { CurrentJackpotValue } from "../models/JackpotCurValue";
import Keyboard from "./Keyboard";
import i18next from "i18next";

export default class SignController {
  private static instance: SignController | null = null;

  private readonly wsClient;
  private readonly wsHandler;
  private readonly sceneManager;

  // TODO: Properly implement this in SceneManager
  private readonly sceneSwitchSeconds = 15000;
  private readonly winTimeSeconds: number = 5000;
  private lastScene: Scene | null = null;
  private sceneSwitchTimer: number | null = null;
  private winTimer: number | null = null;
  private readonly sceneCurJackpot;
  private readonly sceneHistory;
  private readonly sceneJackpotHit;

  // TODO: Move to global store
  private readonly CurValues: CurrentJackpotValue[] = [];

  static init(
    wsClient: WebSocketClient,
    app: Application,
    config: ConfigParams,
  ): SignController {
    if (SignController.instance) {
      throw new Error("SignController is already initialized");
    }
    SignController.instance = new SignController(wsClient, app, config);

    return SignController.instance;
  }

  public static getInstance(): SignController {
    if (!SignController.instance) {
      throw new Error(
        "SignController not initialized. Call SignController.init(...) first.",
      );
    }

    return SignController.instance;
  }

  private constructor(
    wsClient: WebSocketClient,
    app: Application,
    config: ConfigParams,
  ) {
    this.wsClient = wsClient;
    this.wsHandler = new WebSocketMessageHandler(wsClient);
    this.sceneManager = SceneManager(app, config);

    wsClient.onMessage = this.wsHandler.handle;

    this.sceneCurJackpot = new SceneCurrentJackpot({
      app: app,
      config: config,
      name: "CurrentJackpot",
    });
    this.sceneHistory = new SceneHistory({
      app: app,
      config: config,
      name: "JackpotHistory",
    });
    this.sceneJackpotHit = new SceneJackpotHit({
      app: app,
      config: config,
      name: "JackpotHit",
    });

    this.registerScene(this.sceneCurJackpot);
    this.registerScene(this.sceneHistory);
    this.registerScene(this.sceneJackpotHit);

    this.lastScene = this.sceneCurJackpot;
    this.sceneManager.setJackpotHitScene(this.sceneJackpotHit);
    this.sceneManager.switchScene(this.lastScene.name);
    this.updateScene();

    this.registerEventListeners();

    const newHitKey = Keyboard("n");
    newHitKey.press = () => {
      console.log("NEW HIT");
      this.jackpotHit({
        msgType: MessageType.JACKPOT_HIT,
        eventId: "",
        params: {
          jp: 10,
          mach_id: 0,
          name: "TEST",
          timestamp: new Date().toLocaleDateString(i18next.language),
          value: 1000,
          location: "TEST LOCATION",
        },
      });
    };
  }

  private registerEventListeners = (): void => {
    GlobalStore.messageEventEmitter.addListener(
      MessageEvents.MESSAGE_PARAMETERS,
      this.updateParameters,
    );
    GlobalStore.messageEventEmitter.addListener(
      MessageEvents.MESSAGE_JACKPOT_HIT,
      this.jackpotHit,
    );
    GlobalStore.messageEventEmitter.addListener(
      MessageEvents.MESSAGE_JACKPOT_HISTORY,
      this.updateJackpotHistory,
    );
    GlobalStore.messageEventEmitter.addListener(
      MessageEvents.MESSAGE_PROGRESSIVE_CUR_VALUES,
      this.updateCurValues,
    );
  };

  private updateParameters = (msg: ParametersMessage): void => {
    console.log("UPDATE PARAMETERS", msg);
  };

  private jackpotHit = (msg: JackpotHitMessage): void => {
    console.log("JACKPOT HIT", msg);
    this.playWinScene({
      jackpot: msg.params.jp,
      name: msg.params.name,
      machineId: msg.params.mach_id,
      timestamp: new Date(msg.params.timestamp),
      value: msg.params.value,
      location: msg.params.location ?? "",
    });
  };

  private updateJackpotHistory = (msg: JackpotHistoryMessage): void => {
    console.log("UPDATE JACKPOT HISTORY", msg);

    const history = GlobalStore.getJackpotHistory();
    history.splice(0, history.length);
    for (const el of msg.values) {
      history.push({
        jackpot: el.jp,
        location: el.location,
        machineId: el.mach_id,
        timestamp: new Date(el.timestamp),
        name: el.name,
        value: el.value,
      });
    }

    this.sceneHistory.updateHistory(history);
    GlobalStore.setJackpotHistory(history);
    GlobalStore.gameEventEmitter.emit(GameEvents.JACKPOT_HISTORY_UPDATED);
  };

  private updateCurValues = (msg: ProgressiveCurValuesMessage): void => {
    this.sceneCurJackpot.updateValues(msg.values);
  };

  public registerScene = (
    scene: Scene,
    isJackpotHit: boolean = false,
  ): void => {
    scene.hide();
    this.sceneManager.addScene(scene);
    if (isJackpotHit) this.sceneManager.setJackpotHitScene(scene);
  };

  private updateScene(): void {
    if (this.sceneSwitchTimer) {
      clearTimeout(this.sceneSwitchTimer);
      this.sceneSwitchTimer = null;
    }

    const t = setTimeout(() => {
      let newScene = null;
      if (this.lastScene === this.sceneCurJackpot) {
        newScene = this.sceneHistory;
      } else {
        newScene = this.sceneCurJackpot;
      }

      if (newScene) this.sceneManager.switchScene(newScene.name);
      console.log(`[SignController] Scene switch to ${newScene.name}`);
      this.lastScene = newScene;

      this.updateScene();
    }, this.sceneSwitchSeconds);
  }

  private playWinScene(data: JackpotHitData): void {
    if (this.winTimer) {
      return;
    }

    if (this.sceneSwitchTimer) {
      clearTimeout(this.sceneSwitchTimer);
      this.sceneSwitchTimer = null;
    }

    GlobalStore.setJackpotHitData(data);
    this.sceneManager.playHitScene();

    this.winTimer = setTimeout(() => {
      this.updateScene();

      if (this.winTimer) clearTimeout(this.winTimer);
      this.winTimer = null;
    }, this.winTimeSeconds);
  }
}
